'use client';

import type {
  InterfaceNamespaceEntity,
  InterfaceNamespaceTranslationType,
} from '@unifyapps/defs/types/namespace';
// Imported by package name rather than a relative path. That specifier is what matrix
// rewrites in turbopack.resolveAlias to swap in ./backendMatrix.
import { localeBackend } from '@unifyapps/i18n/backend';
import { getSearchDeployedNamespacesQueryOptions } from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import { useQueries } from '@unifyapps/network/react-query';
import type { TFunction, TOptions } from 'i18next';
import i18next from 'i18next';
import type React from 'react';
import { useCallback, useEffect } from 'react';
import type { UseTranslationOptions } from 'react-i18next';
import { initReactI18next, useTranslation as useTranslationOrg } from 'react-i18next';
import { useLanguageContext } from './context/LanguageProvider';
import { getOptions, languages } from './settings';
import {
  getPlatformInterfaceNamespaceSearchRequest,
  getPlatformInterfaceNsArray,
} from './utils/platformInterface';

export type { TFunction } from 'i18next';

export { Trans } from 'react-i18next';

const isServer = typeof window === 'undefined';

// Initialize i18next with React integration and backend resources
i18next
  .use(initReactI18next)
  .use(localeBackend)
  .init({
    ...getOptions(),
    lng: isServer ? undefined : document.documentElement.lang, // Detect language on client side
    preload: isServer ? languages : [],
  })
  .catch((err) => {
    console.error('i18next init', err);
  });

/**
 * Custom hook for translation with server-side support
 * @param ns - Namespace for the translations
 * @param options - Options for the hook
 */
export function useTranslation(ns?: string | string[], options?: UseTranslationOptions<undefined>) {
  const lng = useLanguageContext();

  const platformInterfaceNsArray = getPlatformInterfaceNsArray(ns);
  // Note: We are using useQueries and combining the results to get all the translations, because we want each namespace to have its own queryKey
  //       so that prefetching is useful and namespaces are not again fetched on client.
  //       For eg. if in useTranslation we pass ns = ['ns1', 'ns2'], but while prefetching we do only for 'ns1', then prefetching will be useless because of different queryKey
  //       Check server.ts for prefetching part
  const translations = useQueries({
    queries: platformInterfaceNsArray.map((item) => ({
      ...getSearchDeployedNamespacesQueryOptions(
        getPlatformInterfaceNamespaceSearchRequest(lng, item),
      ),
      staleTime: Infinity,
    })),
    combine: (results) => {
      return results.reduce<Record<string, InterfaceNamespaceTranslationType | undefined>>(
        (acc, curr) => {
          curr.data?.objects?.forEach((item: InterfaceNamespaceEntity) => {
            Object.assign(acc, item.properties.translations);
          });

          return acc;
        },
        {},
      );
    },
  });

  const value = useTranslationOrg(ns); // Original translation hook
  const t: TFunction = value.t;

  // Custom translation function to handle rendering issues.
  // The original useTranslationOrg function has some issues, so we override it.
  // If running on the server, include the language (lng) in the options.
  // on client side, we are assigning in i18n initialization above so we can use it directly here.
  const updatedT: TFunction = useCallback(
    (key: string, _options?: TOptions) => {
      // If we have translations in our platform interface namespace, then give priority to it.
      // As, mostly this will be used to override the labels in different tenants.
      // Otherwise, use "t" function from react-i18next
      if (translations[key]) {
        return translations[key].value;
      }

      const tOptions = { ..._options, lng };

      if (isServer) {
        return t(key, tOptions);
      }
      return t(key, tOptions);
    },
    [translations, t, lng],
  ) as TFunction;

  const i18nLng = value.i18n.language;

  // For the case when we override the language using LanguageContextProvider, we need to load the new language translations
  useEffect(() => {
    if (i18nLng === lng) return;
    void i18next.loadLanguages(lng);
  }, [i18nLng, lng]);

  return {
    ...value,
    t: updatedT, // Use updated translation function
  };
}

export interface ChangeLanguageOptions {
  // No-op on web — kept for signature parity with the native counterpart,
  // which uses it to mark a locale change as bootstrap-induced so the
  // post-reload navigator can drop a stale stored stack. Web has no
  // reload + nav-restore cycle, so the option is ignored here.
  skipStoredNavState?: boolean;
}

export function useChangeLanguage() {
  const { i18n } = useTranslationOrg();

  const setLanguage = useCallback(
    (lng: string, _options?: ChangeLanguageOptions) => {
      try {
        void i18n.changeLanguage(lng);
      } catch (error) {
        console.error('Failed to change language', error);
      }
    },
    [i18n],
  );

  return setLanguage;
}

export function LanguageChangeProvider({ children }: { children: React.ReactNode }) {
  // 'use client';
  // const { i18n } = useTranslationOrg();
  //
  // useKey(
  //   (event) => {
  //     return event.ctrlKey && event.key === 'L';
  //   },
  //   () => {
  //     i18n.changeLanguage(i18n.language === 'en' ? 'de' : 'en').catch((err) => {
  //       console.error('ChangeLanguage', err);
  //     });
  //   },
  // );

  return children;
}

export { i18next };
